import { useCallback, useEffect, useState } from "react";

import {
  cameraLabel,
  pickPreferredCamera,
  readRememberedCamera,
  rememberCamera,
  videoInputsOf,
} from "./cameraDevices";

/**
 * useCameraDevices — enumerate the machine's cameras, remember which one the
 * user picked, and re-enumerate when the hardware changes (QA-105).
 *
 * The pure decisions live in `./cameraDevices`; this hook only performs them
 * against `navigator.mediaDevices`, so a failure here is unambiguously a wiring
 * failure and not a decision one.
 *
 * TWO ENUMERATIONS, ON PURPOSE. Browsers withhold device LABELS until a camera
 * permission has been granted at least once, so the first enumeration typically
 * returns entries whose `label` is "". The hook therefore re-enumerates after a
 * stream has been granted (`refresh()`), which is when the real hardware names
 * become readable — otherwise the dropdown would permanently read "Default
 * camera / Camera 2" even for a user who has granted permission.
 *
 * `devicechange` is listened to so plugging in a USB camera mid-session makes it
 * selectable without a reload, and unplugging the active one does not leave a
 * dead entry selected.
 *
 * @param {MediaDevices} [mediaDevices] injectable for tests
 * @param {Storage} [storage] injectable for tests
 */
const useCameraDevices = (mediaDevices, storage) => {
  const devices =
    mediaDevices || (typeof navigator !== "undefined" ? navigator.mediaDevices : undefined);

  const [cameras, setCameras] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const refresh = useCallback(async () => {
    if (!devices || typeof devices.enumerateDevices !== "function") {
      // An older browser with getUserMedia but no enumerateDevices still gets a
      // working camera — it simply gets no picker, rather than an error.
      setCameras([]);
      return [];
    }
    try {
      const all = await devices.enumerateDevices();
      const inputs = videoInputsOf(all);
      setCameras(inputs);
      setSelectedId((current) => {
        // Keep the user's in-session choice if it is still plugged in; only fall
        // back to the remembered/first camera when it is not.
        if (current && inputs.some((d) => d.deviceId === current)) return current;
        return pickPreferredCamera(inputs, readRememberedCamera(storage));
      });
      return inputs;
    } catch (err) {
      // Enumeration failing must never block capture — the user just loses the
      // picker and gets the browser's default camera.
      setCameras([]);
      return [];
    }
  }, [devices, storage]);

  /** Choose a camera and remember it for next time. */
  const selectCamera = useCallback(
    (deviceId) => {
      setSelectedId(deviceId || null);
      rememberCamera(deviceId, storage);
    },
    [storage]
  );

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled) refresh();
    };
    run();
    if (devices && typeof devices.addEventListener === "function") {
      devices.addEventListener("devicechange", run);
      return () => {
        cancelled = true;
        devices.removeEventListener("devicechange", run);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [devices, refresh]);

  return {
    cameras,
    selectedId,
    selectCamera,
    refresh,
    /** Only worth rendering a picker when there is a real choice to make. */
    hasChoice: cameras.length > 1,
    labelFor: cameraLabel,
  };
};

export default useCameraDevices;
